// ============================================================================
// @torre/expenses — Facturas de plataformas (CC-CONSOLE #957)
// ============================================================================
// Una factura de plataforma puede ser un CFDI (XML/PDF) o un recibo del
// extranjero sin CFDI (Anthropic, GitHub, Google, AWS…), que cuenta como
// comprobante (`factura_extranjera`). La IA (o el XML) lee los datos; aquí se
// decide, sin I/O:
//
//   · a qué plataforma pertenece (alias / proveedor / conceptos),
//   · qué cargo del estado de cuenta le corresponde (importe ± tipo de cambio,
//     fecha cercana), con candidatos para que la persona confirme,
//   · y los faltantes por mes: cargo sin factura o factura sin cargo.
//
// La misma lógica la usa la carga manual (Pago de plataformas) y el buzón de
// facturación (#611) cuando la factura llega por correo.
// ============================================================================

import {
  BASE_CURRENCY,
  daysBetween,
  isIsoDate,
  matchPlatform,
  round2,
  vendorSimilarity,
  type Expense,
  type ExpenseAttachment,
  type InvoiceData,
  type InvoiceStatus,
  type Platform,
} from "./index";

export const EXPENSE_PLATFORM_INVOICES_COLLECTION = "expense_platform_invoices";

export const PLATFORM_INVOICE_KINDS = ["cfdi", "factura_extranjera"] as const;
export type PlatformInvoiceKind = (typeof PLATFORM_INVOICE_KINDS)[number];

/** `por_confirmar`: la IA propuso; una persona debe confirmar plataforma y cargo. */
export const PLATFORM_INVOICE_STATUSES = ["por_confirmar", "confirmada"] as const;
export type PlatformInvoiceStatus = (typeof PLATFORM_INVOICE_STATUSES)[number];

/** Datos de una factura de plataforma. `null` = no venía en el documento. */
export interface PlatformInvoiceData {
  kind: PlatformInvoiceKind;
  vendor: string | null;
  /** RFC del emisor (CFDI) o su identificador fiscal extranjero (EIN, VAT…). */
  vendorTaxId: string | null;
  /** Número de factura/recibo (o serie-folio del CFDI). */
  invoiceNumber: string | null;
  /** Folio fiscal del CFDI (solo `cfdi`). */
  uuid: string | null;
  /** YYYY-MM-DD */
  issueDate: string | null;
  periodFrom: string | null;
  periodTo: string | null;
  subtotal: number | null;
  total: number | null;
  currency: string | null;
  /** Tipo de cambio a pesos que declara el documento. */
  exchangeRate: number | null;
  seats: number | null;
  usageQuantity: number | null;
  usageUnit: string | null;
  concepts: string[];
  readBy: "xml" | "ia" | "manual" | null;
}

export interface ChargeCandidate {
  expenseId: string;
  date: string;
  amount: number;
  currency: string;
  amountBase: number | null;
  vendor: string;
  platformId: string | null;
  score: number;
  reasons: string[];
  /** El cargo es de la misma plataforma o el comercio coincide (no solo monto y fecha). */
  matchesParty?: boolean;
}

export interface PlatformInvoiceProposal {
  platformId: string | null;
  platformReasons: string[];
  /** Cargo propuesto (solo si hay uno claramente mejor). */
  expenseId: string | null;
  candidates: ChargeCandidate[];
}

export interface PlatformInvoice {
  id: string;
  company_id?: string;
  status: PlatformInvoiceStatus;
  platformId: string | null;
  expenseId: string | null;
  /** Mes al que pertenece la factura (YYYY-MM), para los faltantes. */
  period: string | null;
  data: PlatformInvoiceData;
  files: ExpenseAttachment[];
  source: "manual" | "correo";
  proposal?: PlatformInvoiceProposal | null;
  sourceRef?: { type: string; id: string } | null;
  createdAt?: string;
  createdBy?: string | null;
  confirmedAt?: string | null;
  confirmedBy?: string | null;
}

/** Rutas HTTP de Pago de plataformas (se suman a `EXPENSES_ENDPOINTS`). */
export const PLATFORM_BILLING_ENDPOINTS = {
  billing: "/expensesPlatformBilling",
  invoiceList: "/expensesPlatformInvoiceList",
  invoiceUpload: "/expensesPlatformInvoiceUpload",
  invoiceConfirm: "/expensesPlatformInvoiceConfirm",
  invoice: "/expensesPlatformInvoice",
} as const;

// --- Lectura -----------------------------------------------------------------------
const UUID_RE = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/i;
const str = (v: unknown): string | null => {
  const s = v == null ? "" : String(v).trim();
  return s ? s : null;
};
const date10 = (v: unknown): string | null => {
  const s = typeof v === "string" ? v.slice(0, 10) : "";
  return isIsoDate(s) ? s : null;
};
const numOrNull = (v: unknown, { positive = false, integer = false } = {}): number | null => {
  if (v == null || v === "") return null;
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) return null;
  if (positive && n <= 0) return null;
  if (n < 0) return null;
  return integer ? Math.round(n) : n;
};

/** CFDI si trae folio fiscal válido; si no, recibo extranjero. */
export function platformInvoiceKind(data: Pick<PlatformInvoiceData, "uuid">): PlatformInvoiceKind {
  return data.uuid && UUID_RE.test(data.uuid) ? "cfdi" : "factura_extranjera";
}

/** Normaliza lo que devolvió la IA (o lo que capturó la persona). */
export function sanitizePlatformInvoiceData(raw: unknown, readBy: PlatformInvoiceData["readBy"] = "ia"): PlatformInvoiceData {
  const r = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const currency = /^[A-Z]{3}$/.test(String(r.currency ?? "").toUpperCase()) ? String(r.currency).toUpperCase() : null;
  const uuid = str(r.uuid);
  const data: PlatformInvoiceData = {
    kind: "factura_extranjera",
    vendor: str(r.vendor),
    vendorTaxId: str(r.vendorTaxId),
    invoiceNumber: str(r.invoiceNumber),
    uuid: uuid && UUID_RE.test(uuid) ? uuid.toUpperCase() : null,
    issueDate: date10(r.issueDate),
    periodFrom: date10(r.periodFrom),
    periodTo: date10(r.periodTo),
    subtotal: numOrNull(r.subtotal),
    total: numOrNull(r.total, { positive: true }),
    currency: currency === "XXX" ? null : currency,
    exchangeRate: numOrNull(r.exchangeRate, { positive: true }),
    seats: numOrNull(r.seats, { positive: true, integer: true }),
    usageQuantity: numOrNull(r.usageQuantity),
    usageUnit: str(r.usageUnit),
    concepts: Array.isArray(r.concepts) ? r.concepts.map((c) => String(c).trim()).filter(Boolean).slice(0, 20) : [],
    readBy,
  };
  if (data.total != null) data.total = round2(data.total);
  if (data.subtotal != null) data.subtotal = round2(data.subtotal);
  data.kind = platformInvoiceKind(data);
  return data;
}

/** Datos de factura de plataforma a partir de un CFDI (XML) o de la lectura de un PDF. */
export function platformInvoiceFromInvoiceData(inv: InvoiceData & { readBy?: string | null }): PlatformInvoiceData {
  const folio = [inv.serie, inv.folio].filter(Boolean).join("-");
  return sanitizePlatformInvoiceData(
    {
      vendor: inv.emisorNombre,
      vendorTaxId: inv.emisorRfc,
      invoiceNumber: folio || null,
      uuid: inv.uuid,
      issueDate: inv.fecha,
      subtotal: inv.subtotal,
      total: inv.total,
      currency: inv.moneda,
      exchangeRate: inv.tipoCambio,
      concepts: inv.conceptos || [],
    },
    inv.readBy === "xml" ? "xml" : "ia",
  );
}

/** Forma `InvoiceData` para guardar en el gasto al ligarlo (mismo campo que el buzón). */
export function platformInvoiceToInvoiceData(d: PlatformInvoiceData): InvoiceData & { readBy: string | null; kind: PlatformInvoiceKind } {
  return {
    uuid: d.uuid,
    version: null,
    serie: null,
    folio: d.invoiceNumber,
    fecha: d.issueDate,
    subtotal: d.subtotal,
    total: d.total,
    moneda: d.currency,
    tipoCambio: d.exchangeRate,
    emisorRfc: d.vendorTaxId,
    emisorNombre: d.vendor,
    receptorRfc: null,
    receptorNombre: null,
    metodoPago: null,
    formaPago: null,
    conceptos: d.concepts,
    readBy: d.readBy,
    kind: d.kind,
  };
}

/** Estado de factura que toma el gasto ligado: CFDI → `adjunta`; extranjera → `factura_extranjera`. */
export function expenseInvoiceStatusFor(kind: PlatformInvoiceKind): InvoiceStatus {
  return kind === "cfdi" ? "adjunta" : "factura_extranjera";
}

/** Mes de la factura: el de emisión (cuando se cobra); si no, el inicio del periodo. */
export function platformInvoicePeriod(d: Pick<PlatformInvoiceData, "issueDate" | "periodFrom" | "periodTo">): string | null {
  const date = d.issueDate || d.periodFrom || d.periodTo;
  return date ? date.slice(0, 7) : null;
}

// --- Liga con la plataforma ------------------------------------------------------------
export interface PlatformGuess {
  platformId: string;
  reasons: string[];
}

/**
 * Plataforma a la que pertenece la factura: por proveedor (nombre, alias,
 * razón social) y, si no, por los conceptos ("Claude Team plan", "GitHub
 * Copilot Business"). Mismas reglas que `matchPlatform`.
 */
export function matchInvoiceToPlatform(
  d: Pick<PlatformInvoiceData, "vendor" | "vendorTaxId" | "concepts">,
  platforms: readonly Platform[],
  /**
   * `strict` (buzón, sin una persona de por medio): solo nombre exacto o palabra
   * completa, nunca el parecido por prefijo; así «Herederos del Norte» no cae en «HERE».
   */
  opts: { strict?: boolean } = {},
): PlatformGuess | null {
  if (d.vendor) {
    const id = matchPlatform(d.vendor, platforms, !!opts.strict);
    if (id) return { platformId: id, reasons: ["proveedor coincide"] };
  }
  if (d.vendorTaxId) {
    const tax = d.vendorTaxId.toUpperCase();
    const p = platforms.find((x) => x.active !== false && [x.vendor, ...(x.aliases || [])].some((a) => a && a.toUpperCase() === tax));
    if (p) return { platformId: p.id, reasons: ["RFC / id fiscal coincide"] };
  }
  for (const c of d.concepts || []) {
    const id = matchPlatform(c, platforms, !!opts.strict);
    if (id) return { platformId: id, reasons: ["concepto coincide"] };
  }
  return null;
}

// --- Liga con el cargo ---------------------------------------------------------------
export interface ChargeMatchOptions {
  /** Días de diferencia tolerados entre la factura y el cargo. */
  maxDays?: number;
  /** Diferencia relativa tolerada con la misma moneda (0.01 = 1 %). */
  amountTolerance?: number;
  /** Diferencia tolerada al convertir de moneda (spread del banco). */
  fxTolerance?: number;
  /** Tipo de cambio de referencia por moneda ({ USD: 18.4 }). */
  rates?: Record<string, number>;
  /** Gastos ya ligados a otra factura de plataforma (no se proponen). */
  excludeExpenseIds?: readonly string[];
  /** Puntaje mínimo para proponer un cargo sin preguntar. */
  minScore?: number;
}

/**
 * Candidatos de cargo para una factura. Reglas:
 *  - Solo gastos que no sean de nómina/equipo, sin factura ya ligada y que no
 *    pertenezcan a OTRA plataforma.
 *  - Monto: misma moneda dentro de `amountTolerance`; en otra moneda se
 *    convierte con el tipo de cambio (del documento, del gasto o de
 *    referencia) dentro de `fxTolerance`. Sin forma de comparar, no hay
 *    candidato.
 *  - Fecha dentro de la ventana (contra emisión o fin/inicio del periodo).
 *  - Se propone el mejor solo si supera `minScore` y no empata: es mejor
 *    preguntar que ligar la factura al cargo equivocado.
 */
export function proposeChargeForInvoice(
  d: PlatformInvoiceData,
  platformId: string | null,
  expenses: readonly Expense[],
  { maxDays = 10, amountTolerance = 0.01, fxTolerance = 0.08, rates = {}, excludeExpenseIds = [], minScore = 0.6 }: ChargeMatchOptions = {},
): { expenseId: string | null; candidates: ChargeCandidate[] } {
  const total = d.total;
  if (total == null || total <= 0) return { expenseId: null, candidates: [] };
  const invCur = (d.currency || BASE_CURRENCY).toUpperCase();
  const dates = [d.issueDate, d.periodTo, d.periodFrom].filter((x): x is string => Boolean(x && isIsoDate(x)));
  const excluded = new Set(excludeExpenseIds);
  const candidates: ChargeCandidate[] = [];
  for (const e of expenses) {
    if (excluded.has(e.id)) continue;
    if (e.source === "nomina" || e.source === "equipo") continue;
    if (e.invoiceStatus === "adjunta" || e.invoiceStatus === "factura_extranjera" || e.invoice?.uuid) continue;
    if (platformId && e.platformId && e.platformId !== platformId) continue;
    const reasons: string[] = [];
    // --- monto
    const eCur = (e.currency || BASE_CURRENCY).toUpperCase();
    let diff: number | null = null;
    let tol = amountTolerance;
    if (eCur === invCur) {
      diff = Math.abs(e.amount - total) / Math.max(total, 1);
      reasons.push(diff === 0 ? "mismo monto" : "monto dentro de tolerancia");
    } else {
      tol = fxTolerance;
      const invRate = invCur === BASE_CURRENCY ? 1 : d.exchangeRate || rates[invCur] || null;
      const invBase = invRate ? total * invRate : null;
      const eBase = eCur === BASE_CURRENCY ? e.amount : e.amountBase ?? (rates[eCur] ? e.amount * rates[eCur] : null);
      if (invBase != null && eBase != null) {
        diff = Math.abs(eBase - invBase) / Math.max(invBase, 1);
        reasons.push("monto con tipo de cambio");
      }
    }
    if (diff == null || diff > tol) continue;
    // --- fecha
    let gap = maxDays;
    if (dates.length) {
      gap = Math.min(...dates.map((x) => daysBetween(x, e.date)));
      if (gap > maxDays) continue;
      reasons.push(`${gap} día(s) de diferencia`);
    }
    // --- plataforma / comercio
    let who = 0;
    if (platformId && e.platformId === platformId) {
      who = 1;
      reasons.push("cargo de la misma plataforma");
    } else {
      const sim = vendorSimilarity(d.vendor, e.vendor);
      if (sim > 0) {
        who = 0.6 * sim;
        reasons.push("comercio coincide");
      }
    }
    const score = round2(0.35 + 0.25 * who + 0.2 * (1 - gap / (maxDays + 1)) + 0.2 * (1 - diff / Math.max(tol, 0.0001)));
    candidates.push({
      expenseId: e.id,
      date: e.date,
      amount: e.amount,
      currency: eCur,
      amountBase: e.amountBase ?? null,
      vendor: e.vendor,
      platformId: e.platformId || null,
      score,
      reasons,
      matchesParty: who > 0,
    });
  }
  candidates.sort((a, b) => b.score - a.score || a.date.localeCompare(b.date));
  const top = candidates.slice(0, 5);
  const best = top[0];
  const tie = top.length > 1 && top[0].score === top[1].score;
  return { expenseId: best && !tie && best.score >= minScore ? best.expenseId : null, candidates: top };
}

/** Plataforma + cargo propuestos para una factura. */
export function proposePlatformInvoice(
  d: PlatformInvoiceData,
  platforms: readonly Platform[],
  expenses: readonly Expense[],
  options: ChargeMatchOptions & { platformId?: string | null } = {},
): PlatformInvoiceProposal {
  const guess = options.platformId !== undefined ? (options.platformId ? { platformId: options.platformId, reasons: ["elegida por la persona"] } : null) : matchInvoiceToPlatform(d, platforms);
  const platformId = guess ? guess.platformId : null;
  const charge = proposeChargeForInvoice(d, platformId, expenses, options);
  return { platformId, platformReasons: guess ? guess.reasons : [], expenseId: charge.expenseId, candidates: charge.candidates };
}

/** ¿Ya existe esta factura? Mismo folio fiscal o mismo número de la misma plataforma/proveedor. */
export function findDuplicatePlatformInvoice(d: PlatformInvoiceData, platformId: string | null, invoices: readonly PlatformInvoice[]): string | null {
  for (const x of invoices) {
    if (d.uuid && x.data?.uuid && x.data.uuid.toUpperCase() === d.uuid.toUpperCase()) return x.id;
    if (!d.uuid && d.invoiceNumber && x.data?.invoiceNumber && x.data.invoiceNumber.toUpperCase() === d.invoiceNumber.toUpperCase()) {
      const samePlatform = platformId && x.platformId === platformId;
      const sameVendor = vendorSimilarity(d.vendor, x.data.vendor) > 0;
      if (samePlatform || sameVendor) return x.id;
    }
  }
  return null;
}

// --- Faltantes por mes ------------------------------------------------------------------
export type PlatformGapStatus = "ok" | "falta_factura" | "falta_cargo" | "sin_movimiento";

export interface PlatformGapMonth {
  period: string;
  charges: number;
  /** Cargos del mes con factura pendiente. */
  chargesWithoutInvoice: number;
  invoices: number;
  /** Facturas confirmadas del mes sin cargo ligado. */
  invoicesWithoutCharge: number;
  status: PlatformGapStatus;
}

export interface PlatformGapReport {
  platformId: string;
  name: string;
  months: PlatformGapMonth[];
  missingInvoices: number;
  missingCharges: number;
}

export function platformInvoiceGaps(
  platforms: readonly Platform[],
  invoices: readonly PlatformInvoice[],
  expenses: readonly Expense[],
  months: readonly string[],
): PlatformGapReport[] {
  return platforms.map((p) => {
    const own = expenses.filter((e) => e.platformId === p.id && e.source !== "nomina" && e.source !== "equipo");
    const invs = invoices.filter((i) => i.platformId === p.id && i.status === "confirmada");
    const rows = months.map((period) => {
      const charges = own.filter((e) => e.period === period);
      const monthInvoices = invs.filter((i) => i.period === period);
      const chargesWithoutInvoice = charges.filter((e) => e.invoiceStatus === "pendiente").length;
      const invoicesWithoutCharge = monthInvoices.filter((i) => !i.expenseId).length;
      const status: PlatformGapStatus = chargesWithoutInvoice
        ? "falta_factura"
        : invoicesWithoutCharge
          ? "falta_cargo"
          : charges.length || monthInvoices.length
            ? "ok"
            : "sin_movimiento";
      return { period, charges: charges.length, chargesWithoutInvoice, invoices: monthInvoices.length, invoicesWithoutCharge, status };
    });
    return {
      platformId: p.id,
      name: p.name,
      months: rows,
      missingInvoices: rows.reduce((s, r) => s + r.chargesWithoutInvoice, 0),
      missingCharges: rows.reduce((s, r) => s + r.invoicesWithoutCharge, 0),
    };
  });
}
